import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { AlertCircle, ArrowRight, LockKeyhole, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAuth, appRoles, roleLabels } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import artegoIcon from "@/assets/artego-icon.png";
import artegoLogo from "@/assets/artego-logo.png";

function AuthShell({ children, title, subtitle }) {
  return (
    <div className="grid min-h-screen bg-slate-950 lg:grid-cols-[1.05fr_0.95fr]" data-testid="auth-shell">
      <section className="relative hidden overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 lg:block" data-testid="auth-visual-section">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-teal-500/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-blue-600/10 blur-3xl" />
        <div className="absolute inset-x-0 top-0 p-12" data-testid="auth-visual-brand-top">
          <img src={artegoLogo} alt="Artego" className="h-12 w-auto rounded-md bg-white/95 px-4 py-2 object-contain" data-testid="auth-brand-artego" />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-12 text-white" data-testid="auth-visual-copy">
          <div className="mb-6 flex items-center gap-3">
            <img src={artegoIcon} alt="DVR ProSuite" className="h-12 w-12 rounded-md bg-white/95 object-contain" data-testid="auth-logo-large" />
            <div>
              <p className="font-heading text-2xl font-bold" data-testid="auth-brand-name">DVR ProSuite</p>
              <p className="text-sm uppercase tracking-[0.22em] text-teal-200" data-testid="auth-brand-subtitle">by Artego · D.Lgs. 81/2008</p>
            </div>
          </div>
          <h1 className="max-w-2xl font-heading text-5xl font-bold leading-tight" data-testid="auth-hero-title">Governance tecnica per DVR, aziende e ruoli operativi.</h1>
          <p className="mt-5 max-w-xl text-base text-slate-200" data-testid="auth-hero-description">Una base SaaS ordinata per consulenti, RSPP e aziende che devono controllare dati, sedi, accessi e avanzamento documentale.</p>
        </div>
      </section>
      <section className="flex items-center justify-center bg-slate-50 px-4 py-10 sm:px-6" data-testid="auth-form-section">
        <Card className="w-full max-w-md rounded-lg border-slate-200 shadow-sm" data-testid="auth-card">
          <CardHeader>
            <div className="mb-2 flex items-center justify-end" data-testid="auth-lang-row">
              <LanguageSwitcher />
            </div>
            <div className="mb-4 flex items-center gap-3 lg:hidden" data-testid="auth-mobile-brand">
              <img src={artegoIcon} alt="DVR ProSuite by Artego" className="h-10 w-10 rounded-md object-contain" data-testid="auth-logo-mobile" />
              <span className="font-heading text-xl font-bold">DVR ProSuite</span>
            </div>
            <CardTitle className="font-heading text-3xl text-slate-950" data-testid="auth-card-title">{title}</CardTitle>
            <CardDescription data-testid="auth-card-subtitle">{subtitle}</CardDescription>
          </CardHeader>
          <CardContent>{children}</CardContent>
        </Card>
      </section>
    </div>
  );
}

export function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: "", password: "", otp: "" });
  const [needOtp, setNeedOtp] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  if (user) return <Navigate to={user.role === "auditor_esterno" ? "/auditor/" : "/"} replace />;

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(form.email, form.password, form.otp);
      navigate(u?.role === "auditor_esterno" ? "/auditor/" : "/");
    } catch (err) {
      if (/2FA/i.test(err.message) && !form.otp) { setNeedOtp(true); setError(""); } else setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t("auth.login_title")} subtitle={t("auth.login_subtitle")}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {error && <Alert variant="destructive" data-testid="login-error-alert"><AlertCircle className="h-4 w-4" /><AlertTitle>{t("auth.login_error")}</AlertTitle><AlertDescription data-testid="login-error-message">{error}</AlertDescription></Alert>}
        <div className="space-y-2"><Label htmlFor="login-email">{t("auth.email")}</Label><Input id="login-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="login-email-input" /></div>
        <div className="space-y-2"><Label htmlFor="login-password">{t("auth.password")}</Label><Input id="login-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="login-password-input" /></div>
        {needOtp && <div className="space-y-2"><Label htmlFor="login-otp">Codice di verifica (2FA)</Label><Input id="login-otp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={form.otp} onChange={(e) => setForm({ ...form, otp: e.target.value })} required data-testid="login-otp-input" /></div>}
        <Button className="w-full bg-blue-900 hover:bg-blue-800" disabled={loading} data-testid="login-submit-button"><LockKeyhole className="h-4 w-4" /> {loading ? t("auth.login_loading") : t("auth.login_button")}</Button>
        <p className="text-center text-sm text-slate-600" data-testid="login-register-copy">{t("auth.no_account")} <Link className="font-semibold text-blue-900 hover:underline" to="/register" data-testid="go-to-register-link">{t("auth.register_link")}</Link></p>
      </form>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "CONSULENTE", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  if (user) return <Navigate to="/" replace />;

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      navigate("/");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Crea account" subtitle="Il primo account creato diventa amministratore del sistema.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        {error && <Alert variant="destructive" data-testid="register-error-alert"><AlertCircle className="h-4 w-4" /><AlertTitle>Registrazione non riuscita</AlertTitle><AlertDescription data-testid="register-error-message">{error}</AlertDescription></Alert>}
        <div className="space-y-2"><Label htmlFor="register-name">Nome completo</Label><Input id="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="register-name-input" /></div>
        <div className="space-y-2"><Label htmlFor="register-phone">Telefono</Label><Input id="register-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="register-phone-input" /></div>
        <div className="space-y-2"><Label htmlFor="register-email">Email</Label><Input id="register-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="register-email-input" /></div>
        <div className="space-y-2"><Label htmlFor="register-password">Password</Label><Input id="register-password" type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="register-password-input" /></div>
        <div className="space-y-2"><Label htmlFor="register-role">Ruolo richiesto</Label><select id="register-role" className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="register-role-select">{appRoles.filter((role) => role !== "SUPER_ADMIN").map((role) => <option key={role} value={role}>{roleLabels[role]}</option>)}</select></div>
        <Button className="w-full bg-blue-900 hover:bg-blue-800" disabled={loading} data-testid="register-submit-button"><ShieldCheck className="h-4 w-4" /> {loading ? "Creazione..." : "Crea account"} <ArrowRight className="h-4 w-4" /></Button>
        <p className="text-center text-sm text-slate-600" data-testid="register-login-copy">Hai già un account? <Link className="font-semibold text-blue-900 hover:underline" to="/login" data-testid="go-to-login-link">Accedi</Link></p>
      </form>
    </AuthShell>
  );
}